import { Dispatch, SetStateAction, useState } from 'react'

import { toast } from 'react-toastify'

import { IdentifiableObject, WorkorderType } from '../models'
import { axiosInstance } from '../helpers'
import { ProjectAnalytics } from './useEZFolioProject'

export const useEZFolioWorkorderAnalytics = () => {
    const [serviceOrderAnalytics, setServiceOrderAnalytics] = useState<
        ServiceOrderAnalytic[]
    >([])
    const [actionItemAnalytics, setActionItemAnalytics] = useState<
        ActionItemAnalytic[]
    >([])
    const [loadingState, setLoadingState] = useState<AnalyticsLoadingState>({
        mainPage: false,
        detailPage: false,
        commonIssues: false,
        projectAnalytics: false,
    })

    const [
        serviceOrderProjectAnalytics,
        setServiceOrderProjectAnalytics,
    ] = useState<ProjectAnalytics[]>([])

    const [
        serviceRequestDetailAnalytics,
        setServiceRequestDetailAnalytics,
    ] = useState<ServiceRequestDetailAnalytics | null>(null)

    const [
        serviceRequestCommonIssues,
        setServiceRequestCommonIssues,
    ] = useState<ServiceRequestCommonIssues[]>([])

    const [
        actionItemDetailAnalytics,
        setActionItemDetailAnalytics,
    ] = useState<ActionItemDetailAnalytics | null>(null)

    const getServiceOrderAnalytics = async (
        params: GetWorkorderAnalyticsParams,
    ) => {
        try {
            setLoadingState((prev) => ({ ...prev, mainPage: true }))
            const response = await axiosInstance.get(
                '/workorder/workorder-analytics/',
                { params: params },
            )

            const data: ServiceOrderAnalytic[] = response.data
            setServiceOrderAnalytics(data)

            setLoadingState((prev) => ({ ...prev, mainPage: false }))
            return data
        } catch (e) {
            toast.error('Failed to fetch workorder analytics')
            setLoadingState((prev) => ({ ...prev, mainPage: false }))
            return Promise.reject(e)
        }
    }

    const getActionItemAnalytics = async (
        params: GetWorkorderAnalyticsParams,
    ) => {
        try {
            setLoadingState((prev) => ({ ...prev, mainPage: true }))
            const response = await axiosInstance.get(
                '/workorder/workorder-analytics/',
                { params: params },
            )

            const data: ActionItemAnalytic[] = response.data
            setActionItemAnalytics(data)

            setLoadingState((prev) => ({ ...prev, mainPage: false }))
            return data
        } catch (e) {
            toast.error('Failed to fetch workorder analytics')
            setLoadingState((prev) => ({ ...prev, mainPage: false }))
            return Promise.reject(e)
        }
    }

    const getServiceRequestDetailAnalytics = async (
        request: getServiceRequestDetailAnalyticsRequest,
    ) => {
        try {
            setLoadingState((prev) => ({ ...prev, detailPage: true }))
            const response = await axiosInstance.get(
                '/workorder/get_service_request_analytics/',
                { params: request.params },
            )
            const data: ServiceRequestDetailAnalytics = response.data
            setServiceRequestDetailAnalytics(data)
            setLoadingState((prev) => ({ ...prev, detailPage: false }))
            return data
        } catch (e: any) {
            toast.error(e.response.data.message)
            setLoadingState((prev) => ({ ...prev, detailPage: false }))
            return Promise.reject(e)
        }
    }

    const getServiceRequestCommonIssues = async (
        request: getServiceRequestDetailAnalyticsRequest,
    ) => {
        try {
            setLoadingState((prev) => ({ ...prev, commonIssues: true }))
            const response = await axiosInstance.get(
                '/workorder/get_service_request_common_issues/',
                { params: request.params },
            )

            const data: ServiceRequestCommonIssues[] = response.data
            setServiceRequestCommonIssues(data)
            setLoadingState((prev) => ({ ...prev, commonIssues: false }))
            return data
        } catch (e: any) {
            toast.error(e.response.data.message)
            setLoadingState((prev) => ({ ...prev, commonIssues: false }))
            return Promise.reject(e)
        }
    }

    const getServiceOrderProjectAnalytics = async (
        apartmendId: number,
        showArchived: boolean = true,
    ) => {
        try {
            setLoadingState((prev) => ({ ...prev, projectAnalytics: true }))
            const response = await axiosInstance.get(
                `/workorder/get_service_order_project_analytics/`,
                {
                    params: {
                        apartment_id: apartmendId,
                        show_archived: showArchived,
                    },
                },
            )

            const data: ProjectAnalytics[] = response.data
            setServiceOrderProjectAnalytics(data)
            setLoadingState((prev) => ({ ...prev, projectAnalytics: false }))
            return data
        } catch (e) {
            toast.error('Failed to fetch workorder analytics')
            setLoadingState((prev) => ({ ...prev, projectAnalytics: false }))
            return Promise.reject(e)
        }
    }

    const getActionItemDetailAnalytics = async (
        request: getServiceRequestDetailAnalyticsRequest,
    ) => {
        try {
            setLoadingState((prev) => ({ ...prev, detailPage: true }))
            const response = await axiosInstance.get(
                '/workorder/get_action_item_detail_analytics/',
                { params: request.params },
            )
            const data: ActionItemDetailAnalytics = response.data
            setActionItemDetailAnalytics(data)
            setLoadingState((prev) => ({ ...prev, detailPage: false }))
            return data
        } catch (e: any) {
            toast.error(e.response.data.message)
            setLoadingState((prev) => ({ ...prev, detailPage: false }))
            return Promise.reject(e)
        }
    }

    const updateServiceOrderAnalyticsState = (
        serviceOrderAnalytic: ServiceOrderAnalytic,
    ) => {
        _insertOrUpdateIdentifiable(
            serviceOrderAnalytic,
            setServiceOrderAnalytics,
        )
    }

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: Dispatch<SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }

            if (!found) {
                safeOldValue.push(updatedValue)
            }

            return safeOldValue
        })
    }

    return {
        serviceOrderAnalytics,
        actionItemAnalytics,
        serviceRequestDetailAnalytics,
        actionItemDetailAnalytics,
        loadingState,
        serviceRequestCommonIssues,
        serviceOrderProjectAnalytics,
        getServiceOrderAnalytics,
        getActionItemAnalytics,
        getServiceRequestDetailAnalytics,
        getServiceRequestCommonIssues,
        setServiceOrderAnalytics,
        setActionItemAnalytics,
        setServiceRequestDetailAnalytics,
        getServiceOrderProjectAnalytics,
        getActionItemDetailAnalytics,
        updateServiceOrderAnalyticsState,
    }
}

export interface ServiceOrderAnalytic {
    id: number
    apartment_name: string
    total_created: number
    active_count: number
    behind_count: number
    most_recent_date: string | null
    future_count: number
    total_active_schedules: number
    future_schedules: number
}

export interface ActionItemAnalytic {
    id: number
    apartment_name: string
    avg_completed_per_day: number
    avg_days_to_complete: number
    avg_created_per_day: number
    current_open_count: number
    total_created_count: number
    total_completed_count: number
    long_open_count: number
}

export interface GetWorkorderAnalyticsParams {
    workorder_type: WorkorderType
    organization?: number
    start_date?: string
    end_date?: string
}

export interface AnalyticsLoadingState {
    mainPage: boolean
    detailPage: boolean
    commonIssues: boolean
    projectAnalytics: boolean
}

export interface getServiceRequestDetailAnalyticsRequest {
    params: {
        apartment_id: number
        upper_bound_date: string
        lower_bound_date: string
    }
}

interface DailyCount {
    date: string
    count: number
}

export interface ServiceRequestDetailAnalytics {
    top_services: { [key: string]: { date: string; count: number }[] }
    top_tenants: { [key: string]: { date: string; count: number }[] }
    top_locations: { [key: string]: { date: string; count: number }[] }
    vendor_data: {
        [key: string]: {
            assigned_count: number
            completed_count: number
        }
    }
    assigned_daily: DailyCount[]
    completed_daily: DailyCount[]
}

export interface ActionItemDetailAnalytics {
    top_services: { [key: string]: { date: string; count: number }[] }
    top_locations: { [key: string]: { date: string; count: number }[] }
    vendor_data: {
        [key: string]: {
            assigned_count: number
            completed_count: number
        }
    }
    assigned_daily: DailyCount[]
    completed_daily: DailyCount[]
}

export interface ServiceRequestCommonIssues {
    issue: string
    count: number
}
